/* eslint-disable @typescript-eslint/ban-ts-comment */
import { SearchClient } from 'algoliasearch';
import { liteClient as algoliasearch, SearchQuery } from 'algoliasearch/lite';
import {
  ALGOLIA_APP_ID,
  ALGOLIA_APP_KEY,
  ALGOLIA_INDEX_EVENTS_SEARCH,
  AnalyticsPlatformTag,
  AnalyticsSubplatformTag,
  AnalyticsTagKey,
  labelsAnalyticsTags
} from 'constants/algolia';
import {
  createNullCache,
  createBrowserLocalStorageCache,
  createMemoryCache,
  createFallbackableCache
} from '@algolia/client-common';
import { cookiesIdHandler } from 'middleware/cookieMiddlewareIdHandler';
import aa from 'search-insights';
import { isMobile as getIsMobile } from 'utils/misc';

if (!ALGOLIA_APP_ID || !ALGOLIA_APP_KEY || !ALGOLIA_INDEX_EVENTS_SEARCH) {
  throw new Error(
    'Algolia Application ID, API Key and Index must be defined in .env'
  );
}

interface Params {
  query?: string;
  numericFilters?: string[];
  highlightPostTag?: string;
  highlightPreTag?: string;
}

type CustomSearchQuery = SearchQuery & {
  params?: Params;
};

const algoliaClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_APP_KEY, {
  requestsCache: createMemoryCache({ serializable: false }),
  responsesCache: createFallbackableCache({
    caches: [
      createBrowserLocalStorageCache({
        key: 'algolia-responses',
        timeToLive: 600
      }),
      createMemoryCache(),
      createNullCache()
    ]
  })
});

const searchClient: SearchClient = {
  ...algoliaClient,
  // @ts-ignore
  search(requests: CustomSearchQuery[]) {
    // TODO: check the reason for two requests with the difference between highlightPostTag and highlightPreTag
    requests.forEach((request) => {
      delete request?.params?.highlightPostTag;
      delete request?.params?.highlightPreTag;
    });

    const shouldBlockRequest = (request: CustomSearchQuery) => {
      if (request.indexName !== ALGOLIA_INDEX_EVENTS_SEARCH) {
        return false;
      }

      const query = request.params?.query || '';
      const dateFilters = request.params?.numericFilters;

      if (query === '' && (!dateFilters || dateFilters.length === 0))
        return true;

      return false;
    };

    const requestsToExecute = requests.filter(
      (req) => !shouldBlockRequest(req)
    );

    if (requestsToExecute.length === 0) {
      return Promise.resolve({
        results: requests.map((req) => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: req.params?.query || '',
          params: ''
        }))
      });
    }

    return algoliaClient.search(requestsToExecute).then((response) => {
      let executedIndex = 0;

      const results = requests.map((request) => {
        if (shouldBlockRequest(request)) {
          return {
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
            hitsPerPage: 0,
            exhaustiveNbHits: false,
            query: '',
            params: ''
          };
        } else {
          return response.results[executedIndex++];
        }
      });

      return { results };
    });
  }
};

export default searchClient;

let sessionId = cookiesIdHandler().getSessionId();
if (!sessionId) sessionId = cookiesIdHandler().createSessionId();

const initInsightsAlgolia = () => {
  aa('init', {
    appId: ALGOLIA_APP_ID,
    apiKey: ALGOLIA_APP_KEY
  });

  aa('setUserToken', sessionId);
};

initInsightsAlgolia();
export const userTokenAlgolia = sessionId;

export const setAuthenticatedUserTokenAlgolia = () => {
  aa('setAuthenticatedUserToken', sessionId);
};

type TrackViewedObjectIdsParams = {
  index: string;
  objectIDs: string[];
};

type TrackClickedObjectIdsAfterSearchParams = {
  index: string;
  queryID?: string;
  objectID?: string;
  position: number;
};

export const trackViewedObjectIds = ({ index, objectIDs }: TrackViewedObjectIdsParams) => {
  if (!objectIDs.length) {
    return;
  }

  aa('viewedObjectIDs', {
    index,
    eventName: 'Hits Viewed',
    objectIDs
  });
};

export const trackClickedObjectIdsAfterSearch = ({
  index,
  queryID,
  objectID,
  position
}: TrackClickedObjectIdsAfterSearchParams) => {
  if (!queryID || !objectID) {
    return;
  }

  aa('clickedObjectIDsAfterSearch', {
    index,
    eventName: 'Hit Clicked',
    queryID: queryID,
    objectIDs: [objectID],
    positions: [position]
  });
};

function normalizePathSegment(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export type PageType = 'home' | 'search' | 'city' | 'collection' | 'sports';

export function resolveAnalyticsPageTag(
  pathname: string,
  pageType?: PageType | null
): string {
  if (pathname === '/') return labelsAnalyticsTags.home;

  if (pathname === '/sports') return 'sports';

  if (pathname.startsWith('/categorias')) return 'collection';

  if (pathname.startsWith('/eventos')) {
    const pathParts = pathname.split('/').filter(Boolean);
    const slug = pathParts[pathParts.length - 1];

    switch (pageType) {
      case 'search':
        return 'search';
      case 'city':
        return `city_${normalizePathSegment(slug)}`;
      case 'collection':
        return `collection_${normalizePathSegment(slug)}`;
      default:
        return `search`;
    }
  }

  return labelsAnalyticsTags.home;
}

type AnalyticsTagsParams = {
  pageTag: string;
  featureTags: string[];
  subfeatureTag?: string;
  otherTags?: string[];
};

export const getAnalyticsSubplatform = (): AnalyticsSubplatformTag => {
  const isMobile = getIsMobile();

  if (isMobile) {
    if (typeof navigator !== 'undefined') {
      const ua = navigator.userAgent;

      if (/android/i.test(ua)) {
        return AnalyticsSubplatformTag.Android;
      } else if (/iPhone|iPad|iPod/i.test(ua)) {
        return AnalyticsSubplatformTag.IOS;
      }
    }
    return AnalyticsSubplatformTag.Mobile;
  }

  return AnalyticsSubplatformTag.Desktop;
};

export const createAnalyticsTags = (params: AnalyticsTagsParams): string[] => {
  const { pageTag, featureTags, subfeatureTag, otherTags = [] } = params;

  const uniqueFeatureTags = [...new Set(featureTags)].filter(Boolean);

  const rawTags: Omit<Record<AnalyticsTagKey, string | undefined>, AnalyticsTagKey.Feature> = {
    [AnalyticsTagKey.Platform]: AnalyticsPlatformTag.Web,
    [AnalyticsTagKey.Subplatform]: getAnalyticsSubplatform(),
    [AnalyticsTagKey.Page]: pageTag,
    [AnalyticsTagKey.Subfeature]: subfeatureTag
  };

  const analyticsTags = Object.entries(rawTags)
    .filter(([, tagValue]) => Boolean(tagValue))
    .map(([tagKey, tagValue]) => `${tagKey}:${tagValue}`);

  const featureAnalyticsTags = uniqueFeatureTags.map(
    (tagValue) => `${AnalyticsTagKey.Feature}:${tagValue}`
  );

  return [
    ...analyticsTags,
    ...featureAnalyticsTags,
    ...otherTags.filter(Boolean)
  ];
};

export interface SplitHitsParams {
  items: any[];
  isStrictSearchEnabled: boolean;
  userCity?: string;
}

export function splitHitsByCity({
  items,
  isStrictSearchEnabled,
  userCity
}: SplitHitsParams) {
  const shouldSplitHits = !!(isStrictSearchEnabled && userCity);

  if (!shouldSplitHits) {
    return {
      shouldSplitHits: false,
      localEvents: [],
      otherCitiesEvents: []
    };
  }

  const localEvents = items.filter(
    (hit: any) => hit.location?.city === userCity
  );
  const otherCitiesEvents = items.filter(
    (hit: any) => hit.location?.city !== userCity
  );

  return {
    shouldSplitHits: true,
    localEvents,
    otherCitiesEvents
  };
}
